import { AgGridReact } from 'ag-grid-react';
import React, { useCallback, useRef, useEffect, useMemo } from 'react';
import { showModal } from 'erisxkit/client';
import {
  CellClickedEvent,
  GridApi,
  IServerSideDatasource,
  IServerSideGetRowsParams,
} from 'ag-grid-community';
import isEmpty from 'lodash/isEmpty';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAgGridCommon } from 'common/table/agGrid/AgGridCommon';
import User from 'ts/models/User.model';
import getColumnDefs, {
  ACTION_COLUMNS,
  MEMBER_LINK_COL_ID,
  MemberUserGridRow,
} from './columnDefs';
import parseAGRequest from 'utils/agGrid/ssrm/parseAGRequest';
import { commonPaginationOptions, mergeGridOpts } from 'utils/agGrid/options';
import {
  fetchMemberUserTypes,
  fetchMemberUserTypesPromise,
  fetchUsersPromiseCreator,
  getMemoizedMemberUserTypes,
  getMemoizedUserPermissions,
  getUserHasAnyRole,
  selectUser,
  updateUserPromise,
} from 'reducers/usersReducer';
import { useAppSelector } from 'hooks/useAppSelector';
import { ServerSideQuery, ServerSideFilter } from 'utils/agGrid/types';
import { getApiCredentialPermissions } from 'reducers/apiCredentialsReducer';
import history from 'constants/history';
import * as userTypes from 'constants/userTypes';
import { CREATE_MEMBER } from 'constants/modalTypes';
import { Button, Icon } from 'semantic-ui-react';
import styled from 'styled-components';
import { roles } from 'constants/userRoles';
import { MEMBER_USER_GRID_READ_ONLY_ROLES } from 'containers/MemberUsers/MemberUserIndexContainer';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  .add-button {
    width: 200px;
    align-self: flex-end;
    margin-bottom: 0px !important;
  }
  > div {
    margin: 10px 5px 10px 5px;
  }
`;

type FetchUsersRequest = ServerSideQuery & {
  includeName: 'true' | 'false';
};
export const MEMBER_USERS_GRID_NAME = 'memberUsers';

const gridOptions = mergeGridOpts(commonPaginationOptions, {
  rowStyle: { cursor: 'pointer' },
  blockLoadDebounceMillis: 700,
  defaultCsvExportParams: {
    columnKeys: [
      'name',
      'email',
      MEMBER_LINK_COL_ID,
      'createdAt',
      'id',
      'authId',
      'state',
      'disabled',
    ],
  },
});

type MemberUserGridContext = {
  memberUserTypes: { value: string; text: string }[];
  userIds?: string[];
};
interface MemberUserGridProps {
  userIds?: string[];
  onClick: (onComplete: () => void) => void;
}

const MemberUserGrid = ({ userIds, onClick }: MemberUserGridProps) => {
  const gridRef = useRef<AgGridReact>(null);
  const dispatch = useAppDispatch();
  const apiCredentialsPermissions = useAppSelector(getApiCredentialPermissions);
  const memberUserTypes = useAppSelector(getMemoizedMemberUserTypes);
  const userPermissions = useAppSelector(getMemoizedUserPermissions);
  const isReadOnlyRole = useAppSelector((state) =>
    getUserHasAnyRole(state, MEMBER_USER_GRID_READ_ONLY_ROLES),
  );
  const memoizedContext: MemberUserGridContext = useMemo(() => {
    return {
      memberUserTypes,
      userIds,
    };
  }, [memberUserTypes, userIds]);

  const toggleDisable = useCallback(
    (data: User, api: GridApi<User>) => {
      updateUserPromise(
        {
          userId: data.userId,
          update: { disabled: !data.disabled },
        },
        dispatch,
      ).then(() => {
        api.refreshServerSide({ purge: true });
      });
    },
    [dispatch],
  );
  const cloneMemberUser = useCallback(
    (data: User) => {
      dispatch(
        showModal(CREATE_MEMBER, {
          userPermissions,
          apiCredentialsPermissions,
          type: userTypes.memberUser,
          memberUserTypes,
          cloneMember: data,
        }),
      );
    },
    [apiCredentialsPermissions, dispatch, memberUserTypes, userPermissions],
  );

  const columnDefs = useMemo(
    () => getColumnDefs({ toggleDisable, cloneMemberUser, isReadOnlyRole }),
    [toggleDisable, cloneMemberUser, isReadOnlyRole],
  );

  const { gridVisible, initialGridSettings, onStateUpdated, gridButtons } =
    useAgGridCommon({
      gridName: MEMBER_USERS_GRID_NAME,
      gridRef,
    });

  const memoizedDataSourceContext: IServerSideDatasource = useMemo(
    () => ({
      getRows: async ({
        request,
        success,
        context,
      }: IServerSideGetRowsParams<
        MemberUserGridRow,
        MemberUserGridContext
      >) => {
        const { userIds } = context;
        let { memberUserTypes } = context;
        if (isEmpty(memberUserTypes)) {
          memberUserTypes = await fetchMemberUserTypesPromise({}, dispatch);
        }

        const parsed = parseAGRequest(request, { transformAttrs: true });
        const baseFilter: ServerSideFilter[] = [];
        baseFilter.push({
          attr: 'type',
          op: 'eq',
          value: memberUserTypes.map((el: { value: string }) => el.value),
        });
        if (userIds) {
          baseFilter.push({
            attr: 'user_id',
            op: 'eq',
            value: userIds,
          });
        }

        const dynamicFilters: ServerSideFilter[] = parsed?.filter
          ? [...parsed.filter]
          : [];
        const filter: ServerSideFilter[] = [...baseFilter, ...dynamicFilters];

        const body: FetchUsersRequest = {
          ...parsed,
          filter,
          includeName: 'true',
        };
        fetchUsersPromiseCreator(body, dispatch).then((response) => {
          success({
            rowData: response?.users ?? [],
            rowCount: response?.count ?? undefined
          });
        });
      },
    }),
    [dispatch],
  );

  const onCellClicked = useCallback(
    ({ data, colDef }: CellClickedEvent<MemberUserGridRow>) => {
      if (isReadOnlyRole) {
        return;
      }
      const colId = colDef.colId || '';
      if (ACTION_COLUMNS.includes(colId)) {
        return;
      }
      const userId = data?.userId;
      if (userId) {
        dispatch(selectUser(userId));
        history.push(`/member_users/${userId}`);
      }
    },
    [dispatch],
  );

  const isCreating = useMemo(() => !userIds, [userIds]);

  const onComplete = useCallback(() => {
    if (gridRef?.current) {
      gridRef.current.api.refreshServerSide({ purge: true });
    }
  }, [gridRef]);

  const onActionClick = useCallback(
    () => onClick(onComplete),
    [onClick, onComplete],
  );

  return (
    <>
      {gridVisible ? (
        <Wrapper>
          {!isReadOnlyRole && (
            <Button
              onClick={onActionClick}
              className="add-button"
              size="small"
              floated="right"
              disabled={isReadOnlyRole}
            >
              <Icon name="add" /> {isCreating ? 'Create' : 'Add'} Member User
            </Button>
          )}
          {gridButtons}
          <div className="ag-theme-quartz">
            <AgGridReact
              ref={gridRef}
              columnDefs={columnDefs}
              gridOptions={gridOptions}
              onStateUpdated={onStateUpdated}
              initialState={initialGridSettings}
              rowModelType="serverSide"
              onCellClicked={onCellClicked}
              serverSideDatasource={memoizedDataSourceContext}
              context={memoizedContext}
            />
          </div>
        </Wrapper>
      ) : null}
    </>
  );
};

export default MemberUserGrid;
